import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';

export const CollapsibleTriggerSlot = Slot<React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleTrigger>>({
  displayName: 'Collapsible.Trigger',
  preventOverride: true,
  renderDefault: ({ className, ...props }) => (
    <CollapsiblePrimitive.CollapsibleTrigger
      className={cn(
        [
          'focus-visible:ring-2',
          'focus-visible:ring-focus-ring',
          'focus-visible:ring-offset-1',
          'focus-visible:ring-offset-surface-background-0',
        ],
        className,
      )}
      data-spellbook-name="collapsible-trigger"
      {...props}
    />
  ),
});

export const CollapsibleContentSlot = Slot<React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleContent>>({
  displayName: 'Collapsible.Content',
  preventOverride: true,
  renderDefault: ({ className, ...props }) => (
    <CollapsiblePrimitive.CollapsibleContent
      className={cn('w-full data-[state=open]:animate-collapsible-open data-[state=closed]:animate-collapsible-close', className)}
      data-spellbook-name="collapsible-content"
      {...props}
    />
  ),
});

export const CollapsibleTooltipSlot = Slot<
  Omit<TooltipProps, 'asToggleTip'> & {
    'data-testid'?: string;
  }
>({
  displayName: 'Collapsible.Tooltip',
  spellbookName: false,
  preventOverride: true,
  renderDefault: ({ className, children, side = 'top', align = 'center', 'data-testid': dataTestId, ...props }) => {
    const { slots } = useSlots(children, {
      tooltipContent: { Component: CollapsibleTooltipContentSlot },
      trigger: { Component: CollapsibleTriggerSlot, props: { 'data-testid': dataTestId } },
    });

    return (
      <Tooltip className={cn('group', className)} side={side} align={align} asToggleTip={false} {...props}>
        <Tooltip.Trigger>{slots.trigger}</Tooltip.Trigger>
        {slots.tooltipContent && <Tooltip.Content>{slots.tooltipContent}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const CollapsibleTooltipContentSlot = Slot<TextProps>({
  displayName: 'Collapsible.TooltipContent',
  preventOverride: true,
  renderDefault: ({ ...props }) => <Text data-spellbook-name="collapsible-tooltip-content" variant="caption-1" element="span" {...props} />,
});
